import { Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Text, Image } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { getConfigFromStorage } from '@/utils/config';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { UniformGallery } from '@/model';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';
import { useTranslation } from 'react-i18next';

export interface UniformGalleryTableProps {
  data: UniformGallery[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onEditClick: (item: UniformGallery) => void;
  onDeleteClick: (item: UniformGallery) => void;
}

export const UniformGalleryTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  sorting,
  totalItems,
  onSortingChange,
  onPaginationChange,
  onEditClick,
  onDeleteClick,
}: UniformGalleryTableProps) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<{
    team_name: string;
    team_image: string;
    created_at: string;
    title: string;
    season_title: string;
  }>();
  const isMultiTeam = getConfigFromStorage()?.is_multi_team ?? true;

  const columns = [
    columnHelper.accessor('created_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t('pages.application_uniform_gallery.components.uniform_gallery_table.uniform_gallery_table_created_at_header'),
    }),
    columnHelper.accessor('team_name', {
      cell: props => {
        const { team_image, team_name } = props.row.original;
        return (
          <Flex alignItems='center' gap={2}>
            {team_image ? <Image src={team_image} alt={team_name || ''} w='24px' h='24px' objectFit='contain' /> : null}
            <Text fontWeight='bold'>{team_name || ''}</Text>
          </Flex>
        );
      },
      header: t('pages.application_uniform_gallery.components.uniform_gallery_table.uniform_gallery_table_team_header'),
    }),
    columnHelper.accessor('season_title', {
      cell: props => props.getValue(),
      header: t('pages.application_uniform_gallery.components.uniform_gallery_table.uniform_gallery_table_season_header'),
    }),
    columnHelper.accessor('title', {
      cell: props => props.getValue(),
      header: t('pages.application_uniform_gallery.components.uniform_gallery_table.uniform_gallery_table_title_header'),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('pages.application_uniform_gallery.components.uniform_gallery_table.uniform_gallery_table_actions_header'),
      cell: props => (
        <Flex justifyContent={'end'}>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t(
                'pages.application_uniform_gallery.components.uniform_gallery_table.uniform_gallery_table_actions_aria_label',
              )}
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => onEditClick?.(props.row.original as UniformGallery)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='eye' />
                  </Text>
                }
              >
                <Text>
                  {t('pages.application_uniform_gallery.components.uniform_gallery_table.uniform_gallery_table_view_details')}
                </Text>
              </MenuItem>
              <MenuItem
                onClick={() => onDeleteClick?.(props.row.original as UniformGallery)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='trash-2' color='red.500' w='18px' />
                  </Text>
                }
              >
                <Text>
                  {t('pages.application_uniform_gallery.components.uniform_gallery_table.uniform_gallery_table_delete')}
                </Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={isMultiTeam ? columns : columns.filter(col => (col as any).accessorKey !== 'team_name')}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
